import { useState, useEffect } from "react";
import { usersApi } from "./api";

const ROLES = [
  { value: "admin",      label: "Administrateur", desc: "Accès complet — gestion, création, suppression",    color: "#a855f7", icon: "👑" },
  { value: "technicien", label: "Technicien",     desc: "Interventions assignées, stock, rapports terrain",  color: "#5b6ef8", icon: "🔧" },
  { value: "dispatcher", label: "Dispatcher",     desc: "Planification et assignation des interventions",    color: "#22d3a0", icon: "📋" },
  { value: "readonly",   label: "Lecture seule",  desc: "Consultation uniquement, aucune modification",      color: "#f5a623", icon: "👁️" },
];

const STATUTS = [
  { value: "actif",   label: "Actif",   color: "#22d3a0" },
  { value: "inactif", label: "Inactif", color: "#ef4444" },
];

export default function Users({ T }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // null | 'create' | user_obj
  const [pwModal, setPwModal]     = useState(null);
  const [filterRole, setFilterRole] = useState("all");
  const [search, setSearch]       = useState("");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");

  const [form, setForm] = useState({ prenom:"", nom:"", email:"", password:"", role:"technicien", telephone:"", statut:"actif" });
  const [pwForm, setPwForm] = useState({ password:"", confirm:"" });

  const load = async () => {
    setLoading(true);
    const data = await usersApi.getAll();
    setUsers(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ prenom:"", nom:"", email:"", password:"", role:"technicien", telephone:"", statut:"actif" });
    setError(""); setSuccess("");
    setModal("create");
  };

  const openEdit = (u) => {
    setForm({ prenom:u.prenom, nom:u.nom, email:u.email, password:"", role:u.role, telephone:u.telephone||"", statut:u.statut });
    setError(""); setSuccess("");
    setModal(u);
  };

  const handleSave = async () => {
    if (!form.prenom || !form.nom || !form.email || !form.role) { setError("Tous les champs marqués * sont obligatoires"); return; }
    if (modal === "create" && !form.password) { setError("Le mot de passe est obligatoire"); return; }
    setSaving(true); setError("");
    try {
      let res;
      if (modal === "create") {
        res = await usersApi.create(form);
      } else {
        res = await usersApi.update(modal.id, { prenom:form.prenom, nom:form.nom, role:form.role, telephone:form.telephone, statut:form.statut });
      }
      if (res.error) { setError(res.error); setSaving(false); return; }
      setSuccess(modal === "create" ? `✅ ${res.prenom} ${res.nom} créé avec succès !` : "✅ Utilisateur mis à jour !");
      await load();
      setTimeout(() => { setModal(null); setSuccess(""); }, 1200);
    } catch(e) { setError("Erreur serveur"); }
    setSaving(false);
  };

  const handlePassword = async () => {
    if (pwForm.password.length < 6) { setError("Minimum 6 caractères"); return; }
    if (pwForm.password !== pwForm.confirm) { setError("Les mots de passe ne correspondent pas"); return; }
    setSaving(true); setError("");
    const res = await usersApi.changePassword(pwModal.id, pwForm.password);
    if (res.error) { setError(res.error); setSaving(false); return; }
    setSuccess("✅ Mot de passe modifié !");
    setTimeout(() => { setPwModal(null); setSuccess(""); setError(""); }, 1200);
    setSaving(false);
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Désactiver le compte de ${u.prenom} ${u.nom} ?`)) return;
    await usersApi.deactivate(u.id);
    await load();
  };

  const filtered = users.filter(u =>
    (filterRole === "all" || u.role === filterRole) &&
    (`${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
  );

  const getRoleInfo = (r) => ROLES.find(x => x.value === r) || { label: r, color: T.muted, icon: "👤" };

  const inp = (style={}) => ({
    background: T.bg, border: `1px solid ${T.border}`, borderRadius: 9,
    padding: "10px 13px", color: T.text, fontSize: 14, outline: "none", width: "100%", ...style
  });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:22 }}>

      {/* HEADER */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div>
          <div style={{ fontSize:25, fontWeight:800, color:T.text, letterSpacing:"-0.7px" }}>Gestion des utilisateurs</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:4 }}>{users.length} utilisateur(s) · Rôles et permissions</div>
        </div>
        <button onClick={openCreate} style={{ background:T.accent, border:"none", borderRadius:9, padding:"10px 18px", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
          ＋ Nouvel utilisateur
        </button>
      </div>

      {/* RÔLES CARDS */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {ROLES.map(r => {
          const count = users.filter(u => u.role === r.value).length;
          return (
            <div key={r.value} onClick={() => setFilterRole(filterRole === r.value ? "all" : r.value)}
              style={{ background:T.card, border:`1px solid ${filterRole === r.value ? r.color : T.border}`, borderRadius:13, padding:"15px 17px", cursor:"pointer", transition:"border 0.15s", boxShadow: filterRole===r.value?`0 0 0 3px ${r.color}22`:"none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <span style={{ fontSize:22 }}>{r.icon}</span>
                <span style={{ fontSize:20, fontWeight:800, color:r.color }}>{count}</span>
              </div>
              <div style={{ fontSize:13, fontWeight:700, color:T.text, marginTop:8 }}>{r.label}</div>
              <div style={{ fontSize:11, color:T.muted, marginTop:3, lineHeight:1.4 }}>{r.desc}</div>
            </div>
          );
        })}
      </div>

      {/* FILTRES */}
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"7px 13px", flex:1, maxWidth:360 }}>
          <span>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un utilisateur..." style={{ background:"none", border:"none", outline:"none", color:T.text, fontSize:13, flex:1 }}/>
        </div>
        <button onClick={() => setFilterRole("all")} style={{ padding:"7px 14px", borderRadius:8, border:`1px solid ${filterRole==="all"?T.accent:T.border}`, background:filterRole==="all"?T.accentDim:"transparent", color:filterRole==="all"?T.accent:T.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>
          Tous ({users.length})
        </button>
        <button onClick={load} style={{ padding:"7px 13px", borderRadius:8, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:13, cursor:"pointer" }}>↻</button>
      </div>

      {/* TABLE */}
      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:T.muted }}>⏳ Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:40, color:T.muted }}>Aucun utilisateur trouvé</div>
      ) : (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:15, overflow:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:700 }}>
            <thead>
              <tr style={{ background:T.bg }}>
                {["Utilisateur","Email","Rôle","Téléphone","Statut","Actions"].map(h => (
                  <th key={h} style={{ padding:"13px 16px", textAlign:"left", fontSize:11, fontWeight:700, color:T.muted, letterSpacing:"0.7px", textTransform:"uppercase", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const role = getRoleInfo(u.role);
                const isInactive = u.statut === "inactif";
                return (
                  <tr key={u.id} style={{ borderBottom:`1px solid ${T.border}22`, opacity: isInactive ? 0.55 : 1 }}>
                    <td style={{ padding:"13px 16px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:36, height:36, borderRadius:"50%", background:`${role.color}22`, border:`1.5px solid ${role.color}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:role.color, flexShrink:0 }}>
                          {u.prenom[0]}{u.nom[0]}
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{u.prenom} {u.nom}</div>
                          <div style={{ fontSize:10, color:T.muted }}>Créé le {new Date(u.created_at).toLocaleDateString('fr-FR')}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:"13px 16px", fontSize:12, color:T.mutedL, fontFamily:"monospace" }}>{u.email}</td>
                    <td style={{ padding:"13px 16px" }}>
                      <span style={{ fontSize:11, fontWeight:700, color:role.color, background:`${role.color}20`, padding:"3px 10px", borderRadius:16, display:"inline-flex", alignItems:"center", gap:5 }}>
                        {role.icon} {role.label}
                      </span>
                    </td>
                    <td style={{ padding:"13px 16px", fontSize:12, color:T.muted }}>{u.telephone || "—"}</td>
                    <td style={{ padding:"13px 16px" }}>
                      <span style={{ fontSize:11, fontWeight:700, color: u.statut==="actif"?T.success:T.danger, background:`${u.statut==="actif"?T.success:T.danger}22`, padding:"3px 10px", borderRadius:20 }}>
                        {u.statut==="actif" ? "● Actif" : "● Inactif"}
                      </span>
                    </td>
                    <td style={{ padding:"13px 16px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={() => openEdit(u)} style={{ padding:"5px 11px", borderRadius:7, border:`1px solid ${T.accent}50`, background:T.accentDim, color:T.accent, fontSize:11, fontWeight:600, cursor:"pointer" }}>✏️ Modifier</button>
                        <button onClick={() => { setPwModal(u); setPwForm({password:"",confirm:""}); setError(""); }} style={{ padding:"5px 11px", borderRadius:7, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:11, cursor:"pointer" }}>🔑 MDP</button>
                        {u.statut==="actif" && (
                          <button onClick={() => handleDeactivate(u)} style={{ padding:"5px 11px", borderRadius:7, border:`1px solid ${T.danger}40`, background:T.dangerDim, color:T.danger, fontSize:11, cursor:"pointer" }}>🚫</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL CRÉER / MODIFIER */}
      {modal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:20, padding:30, width:520, maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:T.text }}>{modal==="create" ? "Nouvel utilisateur" : `Modifier — ${modal.prenom} ${modal.nom}`}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>Remplissez les informations et assignez un rôle</div>
              </div>
              <button onClick={()=>setModal(null)} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:20 }}>✕</button>
            </div>

            {error && <div style={{ background:`${T.danger}15`, border:`1px solid ${T.danger}40`, borderRadius:9, padding:"10px 14px", marginBottom:14, fontSize:13, color:T.danger }}>⚠ {error}</div>}
            {success && <div style={{ background:`${T.success}15`, border:`1px solid ${T.success}40`, borderRadius:9, padding:"10px 14px", marginBottom:14, fontSize:13, color:T.success }}>{success}</div>}

            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[["Prénom *","prenom","Prénom"],["Nom *","nom","Nom"]].map(([label,key,ph])=>(
                  <div key={key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>{label}</label>
                    <input value={form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))} placeholder={ph} style={inp()}/>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>Email *</label>
                <input value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="prenom.nom@gs-group.fr" type="email" disabled={modal!=="create"} style={inp({opacity:modal!=="create"?0.6:1})}/>
              </div>
              {modal === "create" && (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>Mot de passe *</label>
                  <input value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="Minimum 6 caractères" type="password" style={inp()}/>
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>Téléphone</label>
                <input value={form.telephone} onChange={e=>setForm(p=>({...p,telephone:e.target.value}))} placeholder="06 XX XX XX XX" style={inp()}/>
              </div>

              {/* RÔLE */}
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>Rôle & Permissions *</label>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {ROLES.map(r => (
                    <div key={r.value} onClick={() => setForm(p=>({...p,role:r.value}))}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:11, border:`1.5px solid ${form.role===r.value?r.color:T.border}`, background:form.role===r.value?`${r.color}12`:"transparent", cursor:"pointer", transition:"all 0.15s" }}>
                      <span style={{ fontSize:20 }}>{r.icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:form.role===r.value?r.color:T.text }}>{r.label}</div>
                        <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{r.desc}</div>
                      </div>
                      <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${form.role===r.value?r.color:T.border}`, background:form.role===r.value?r.color:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {form.role===r.value && <div style={{ width:6, height:6, borderRadius:"50%", background:"#fff" }}/>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* STATUT (edit uniquement) */}
              {modal !== "create" && (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>Statut du compte</label>
                  <div style={{ display:"flex", gap:10 }}>
                    {STATUTS.map(s => (
                      <div key={s.value} onClick={()=>setForm(p=>({...p,statut:s.value}))}
                        style={{ flex:1, padding:"10px 14px", borderRadius:10, border:`1.5px solid ${form.statut===s.value?s.color:T.border}`, background:form.statut===s.value?`${s.color}15`:"transparent", cursor:"pointer", textAlign:"center" }}>
                        <div style={{ fontSize:13, fontWeight:700, color:form.statut===s.value?s.color:T.text }}>● {s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display:"flex", gap:10, marginTop:22 }}>
              <button onClick={()=>setModal(null)} style={{ flex:1, padding:"11px", borderRadius:10, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:14, fontWeight:600, cursor:"pointer" }}>Annuler</button>
              <button onClick={handleSave} disabled={saving} style={{ flex:2, padding:"11px", borderRadius:10, background:`linear-gradient(135deg,${T.accent},${T.purple})`, border:"none", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", opacity:saving?0.7:1 }}>
                {saving ? "Enregistrement..." : modal==="create" ? "✓ Créer l'utilisateur" : "✓ Enregistrer les modifications"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MOT DE PASSE */}
      {pwModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:20, padding:30, width:420 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
              <div>
                <div style={{ fontSize:17, fontWeight:800, color:T.text }}>🔑 Changer le mot de passe</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>{pwModal.prenom} {pwModal.nom}</div>
              </div>
              <button onClick={()=>{setPwModal(null);setError("");}} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:20 }}>✕</button>
            </div>
            {error && <div style={{ background:`${T.danger}15`, border:`1px solid ${T.danger}40`, borderRadius:9, padding:"10px 14px", marginBottom:14, fontSize:13, color:T.danger }}>⚠ {error}</div>}
            {success && <div style={{ background:`${T.success}15`, border:`1px solid ${T.success}40`, borderRadius:9, padding:"10px 14px", marginBottom:14, fontSize:13, color:T.success }}>{success}</div>}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {[["Nouveau mot de passe","password"],["Confirmer","confirm"]].map(([label,key])=>(
                <div key={key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.mutedL, textTransform:"uppercase", letterSpacing:"0.7px" }}>{label}</label>
                  <input value={pwForm[key]} onChange={e=>setPwForm(p=>({...p,[key]:e.target.value}))} type="password" placeholder="••••••••" style={inp()}/>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:10, marginTop:20 }}>
              <button onClick={()=>{setPwModal(null);setError("");}} style={{ flex:1, padding:"11px", borderRadius:10, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, fontSize:14, fontWeight:600, cursor:"pointer" }}>Annuler</button>
              <button onClick={handlePassword} disabled={saving} style={{ flex:2, padding:"11px", borderRadius:10, background:T.accent, border:"none", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>
                {saving ? "..." : "✓ Modifier"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
